import { useEffect, useState } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "../lib/api";
import LazyImage from "./LazyImage";

/**
 * Public "Espacio" section — gallery of photos from the studio / local.
 * Photos are fully managed from the admin panel (collection: space).
 * Renders nothing if there are no items yet, so the section stays clean
 * until Silvia uploads her first photos of the space.
 */
export default function Space() {
    const [items, setItems] = useState([]);
    const [lightboxIdx, setLightboxIdx] = useState(null);

    useEffect(() => {
        let mounted = true;
        const fetchItems = () => {
            api.get(`/space?t=${Date.now()}`)
                .then(({ data }) => {
                    if (mounted && Array.isArray(data)) setItems(data);
                })
                .catch(() => {});
        };
        fetchItems();
        const onVis = () => {
            if (document.visibilityState === "visible") fetchItems();
        };
        document.addEventListener("visibilitychange", onVis);
        window.addEventListener("focus", fetchItems);
        return () => {
            mounted = false;
            document.removeEventListener("visibilitychange", onVis);
            window.removeEventListener("focus", fetchItems);
        };
    }, []);

    const close = () => setLightboxIdx(null);
    const next = () =>
        setLightboxIdx((i) => (i === null ? 0 : (i + 1) % items.length));
    const prev = () =>
        setLightboxIdx((i) =>
            i === null ? 0 : (i - 1 + items.length) % items.length,
        );

    if (items.length === 0) {
        return (
            <section
                id="espacio"
                data-testid="space-section"
                className="relative py-14 sm:py-20 bg-[#f4f1ea]"
            >
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="text-center max-w-3xl mx-auto reveal">
                        <span className="text-[10px] tracking-[0.4em] uppercase text-[#C4A882]">
                            El espacio
                        </span>
                        <h2
                            className="font-display text-4xl sm:text-5xl lg:text-6xl font-light text-[#1C1A18] mt-4 leading-[1.1]"
                            data-testid="space-title"
                        >
                            Un lugar pensado para ti
                        </h2>
                        <p
                            className="mt-6 text-[#6b665f] font-light"
                            data-testid="space-subtitle"
                        >
                            Mi estudio en el País Vasco — luz natural, calma y
                            detalles cuidados para que cada sesión sea única.
                        </p>
                        <div className="mt-10 flex justify-center">
                            <span className="gold-line" aria-hidden="true" />
                        </div>
                        <p className="mt-10 text-xs tracking-[0.32em] uppercase text-[#6b665f]/70">
                            Fotos del estudio · Próximamente
                        </p>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section
            id="espacio"
            data-testid="space-section"
            className="relative py-14 sm:py-20 bg-[#f4f1ea]"
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-12">
                <div className="text-center max-w-3xl mx-auto reveal">
                    <span className="text-[10px] tracking-[0.4em] uppercase text-[#C4A882]">
                        El espacio
                    </span>
                    <h2
                        className="font-display text-4xl sm:text-5xl lg:text-6xl font-light text-[#1C1A18] mt-4 leading-[1.1]"
                        data-testid="space-title"
                    >
                        Un lugar pensado para ti
                    </h2>
                    <p
                        className="mt-6 text-[#6b665f] font-light"
                        data-testid="space-subtitle"
                    >
                        Mi estudio en el País Vasco — luz natural, calma y
                        detalles cuidados para que cada sesión sea única.
                    </p>
                </div>

                <div
                    className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 auto-rows-[180px] sm:auto-rows-[220px] lg:auto-rows-[260px] portfolio-grid-fade"
                    data-testid="space-grid"
                >
                    {items.map((img, idx) => (
                        <button
                            type="button"
                            key={img.id}
                            className="photo-card"
                            onClick={() => setLightboxIdx(idx)}
                            data-testid={`space-item-${img.id}`}
                            aria-label={img.caption || "Ver foto del espacio"}
                        >
                            <LazyImage
                                src={img.image}
                                alt={
                                    img.caption ||
                                    "Estudio de Silvia García Fotografía"
                                }
                                className="w-full h-full object-cover"
                            />
                            {img.caption ? (
                                <div className="photo-overlay">
                                    <span className="text-white text-xs tracking-[0.4em] uppercase font-light text-center px-3">
                                        {img.caption}
                                    </span>
                                </div>
                            ) : null}
                        </button>
                    ))}
                </div>
            </div>

            {lightboxIdx !== null && (
                <div
                    role="dialog"
                    aria-modal="true"
                    data-testid="space-lightbox"
                    className="fixed inset-0 z-[60] bg-[#1C1A18]/95 flex items-center justify-center p-4"
                    onClick={close}
                >
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            close();
                        }}
                        aria-label="Cerrar"
                        data-testid="space-lightbox-close"
                        className="absolute top-6 right-6 text-white p-2"
                    >
                        <X size={28} />
                    </button>
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            prev();
                        }}
                        aria-label="Anterior"
                        data-testid="space-lightbox-prev"
                        className="absolute left-4 sm:left-10 text-white p-2"
                    >
                        <ChevronLeft size={36} />
                    </button>
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            next();
                        }}
                        aria-label="Siguiente"
                        data-testid="space-lightbox-next"
                        className="absolute right-4 sm:right-10 text-white p-2"
                    >
                        <ChevronRight size={36} />
                    </button>

                    <div
                        className="max-w-5xl w-full max-h-[85vh] flex flex-col items-center"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <LazyImage
                            src={items[lightboxIdx].image}
                            alt={
                                items[lightboxIdx].caption ||
                                "Estudio de Silvia García"
                            }
                            eager
                            className="max-h-[80vh] w-auto object-contain"
                            data-testid="space-lightbox-image"
                        />
                        {items[lightboxIdx].caption ? (
                            <span className="mt-4 text-white/80 text-xs tracking-[0.4em] uppercase">
                                {items[lightboxIdx].caption}
                            </span>
                        ) : null}
                    </div>
                </div>
            )}
        </section>
    );
}
